import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'
import { CommonModule } from '@angular/common'
import {
    SHIKSHA_DASHBOARD_SUBTITLE_HEADER_UI,
    SHIKSHA_TEAM_CONTRIBUTION_CONTENT,
    SHIKSHA_TEAM_CONTRIBUTION_UI,
} from '../constants/shiksha-dashboard.constants'
import type {
    IShikshaTeamContributionMember,
    IShikshaTeamContributionViewModel,
} from '@features/home/models/shiksha-dashboard.model'
import { ShikshaDashboardSubtitleHeaderComponent } from '../shiksha-dashboard-subtitle-header/shiksha-dashboard-subtitle-header.component'

@Component({
    selector: 'app-shiksha-team-contribution',
    standalone: true,
    templateUrl: './shiksha-team-contribution.component.html',
    styleUrl: './shiksha-team-contribution.component.scss',
    imports: [CommonModule, ShikshaDashboardSubtitleHeaderComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShikshaTeamContributionComponent {
    readonly content = SHIKSHA_TEAM_CONTRIBUTION_CONTENT
    readonly uiConfig = SHIKSHA_TEAM_CONTRIBUTION_UI
    readonly subtitleHeaderUi = SHIKSHA_DASHBOARD_SUBTITLE_HEADER_UI
    readonly members = input<readonly IShikshaTeamContributionMember[]>([])

    readonly viewMembers = computed<readonly IShikshaTeamContributionViewModel[]>(() =>
        this.members().map((member) => ({
            ...member,
            initials: this.getMemberInitials(member.name),
        })),
    )

    readonly contributionRows = computed<
        readonly {
            rank: number
            name: string
            initials: string
            closedRevenue: string
            pipeline: string
            winRate: string
            contributionPercent: number
            rankToneClass: string
        }[]
    >(() =>
        this.viewMembers().map((member, index) => {
            const apiWinRate = member.winRatePercent
            const parsedFromConversion = (() => {
                const rawWinRateMatch = member.conversion.match(/([\d.]+)%/)
                return rawWinRateMatch?.[1] ? Number(rawWinRateMatch[1]) : NaN
            })()
            const resolvedWinRate =
                apiWinRate != null && Number.isFinite(apiWinRate)
                    ? apiWinRate
                    : parsedFromConversion
            const winRate = Number.isFinite(resolvedWinRate)
                ? `${resolvedWinRate.toFixed(1)}${this.content.percentSymbol}`
                : this.content.unavailableCellValue

            const pipelinePrefixPattern = new RegExp(
                `^\\s*${SHIKSHA_TEAM_CONTRIBUTION_CONTENT.pipelineLabelPrefix}\\s*`,
                'i',
            )
            const pipeline = member.pipeline.replace(pipelinePrefixPattern, '').trim()
            const closedRevenue = member.closedRevenue?.trim() || this.content.unavailableCellValue
            const contributionPercent = Math.max(0, Math.min(member.teamRevenuePercent, 100))
            const rank = member.contributionRank ?? index + 1

            return {
                rank,
                name: member.name,
                initials: member.initials,
                closedRevenue,
                pipeline,
                winRate,
                contributionPercent,
                rankToneClass:
                    index === 0
                        ? 'shiksha-team-contribution__rank--top'
                        : index === 1
                          ? 'shiksha-team-contribution__rank--runner-up'
                          : 'shiksha-team-contribution__rank--default',
            }
        }),
    )
    readonly hasRows = computed(() => this.contributionRows().length > 0)

    private getMemberInitials(name: string): string {
        const normalizedName = name.trim()
        if (!normalizedName) {
            return this.content.fallbackInitials
        }

        const nameParts = normalizedName.split(/\s+/)
        if (nameParts.length === 1) {
            return nameParts[0].slice(0, 2).toUpperCase()
        }

        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
    }
}
